import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ChatSideBarSelection, TEXT_MAP } from './ChatRightSideBar'
import { PencilSparkleIcon } from '../../atoms/icons/PencilSparkleIcon'
import { ResourcesIcon } from '../../atoms/icons/ResourcesIcon'
import { RulerAndPencilIconFilled } from '../../atoms/icons/RulerAndPencilIconFilled'
import { SideNavClientIcon } from '../../atoms/icons/SideNavClientIcon'
import { SideNavDocumentIcon } from '../../atoms/icons/SideNavDocumentIcon'
import { SparkleSummariesIcon } from '../../atoms/icons/SparkleSummariesIcon'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { tID } from '../../utils/utils'

const IconGroupContainer = styled.View({
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
})

type IconConfig = {
  component: React.ComponentType<{
    size?: number | string
    color?: string
    fillColor?: string
    isFilled?: boolean
  }>
  size: number
}

export type SideBarIconGroupProps = {
  itemsToShow: ChatSideBarSelection[]
  selectedItem: ChatSideBarSelection
  onItemSelected: (item: ChatSideBarSelection) => void
}

export const SideBarIconGroup: React.FC<SideBarIconGroupProps> = ({ itemsToShow, selectedItem, onItemSelected }) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  const items = useMemo(() => {
    // Define ICON_MAP inside useMemo to avoid circular dependency at module load time
    const ICON_MAP: Record<ChatSideBarSelection, IconConfig> = {
      [ChatSideBarSelection.CLIENT_DETAILS]: {
        component: SideNavClientIcon,
        size: 26, // Larger to compensate for built-in padding in 32x32 viewBox
      },
      [ChatSideBarSelection.TEMPLATES]: {
        component: RulerAndPencilIconFilled,
        size: 24,
      },
      [ChatSideBarSelection.SMART_SUGGESTIONS]: {
        component: PencilSparkleIcon,
        size: 24,
      },
      [ChatSideBarSelection.SUMMARIES]: {
        component: SparkleSummariesIcon,
        size: 24,
      },
      [ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS]: {
        component: ResourcesIcon,
        size: 24,
      },
      [ChatSideBarSelection.DOCUMENT_LIBRARY]: {
        component: SideNavDocumentIcon,
        size: 24,
      },
    }

    return itemsToShow.map((item) => {
      const config = ICON_MAP[item]
      const IconComponent = config.component
      const isSelected = selectedItem === item
      const iconColor = isSelected ? colors.iconActive : colors.iconDefault

      const iconProps = { size: config.size, isFilled: isSelected, fillColor: iconColor }

      const tooltipContent = formatMessage(TEXT_MAP[item])

      return {
        item,
        IconComponent,
        iconProps,
        tooltipContent,
      }
    })
  }, [itemsToShow, selectedItem, colors.iconActive, colors.iconDefault, formatMessage])

  return (
    <IconGroupContainer testID={tID('ChatRightSideBar-icon-group')}>
      {items.map(({ item, IconComponent, iconProps, tooltipContent }) => (
        <Tooltip key={item} content={tooltipContent} placement='bottom' hoverEnabled asChild>
          <TertiaryIconButton
            testID={tID(`ChatRightSideBar-icon-${item}`)}
            leftIcon={<IconComponent {...iconProps} />}
            iconColor={iconProps.fillColor}
            onPress={() => onItemSelected(item)}
            accessibilityLabel={tooltipContent}
          />
        </Tooltip>
      ))}
    </IconGroupContainer>
  )
}
